/**
 * Irrigation Zone Management API Service
 *
 * This service provides methods for interacting with irrigation zone
 * backend endpoints including zone CRUD operations and sector membership
 * management.
 */

import apiClient from './apiClient';
import type {
  IrrigationZone,
  IrrigationZoneCreate,
  IrrigationZoneUpdate,
  ZoneSectorMembership,
  IrrigationZoneWithSectors,
  IrrigationZoneListResponse,
  ZoneSectorsListResponse,
} from '@/types/farms';

class IrrigationZonesApiService {
  /**
   * Get all irrigation zones for a farm
   */
  async getIrrigationZones(
    farmId: string,
    params?: {
      skip?: number;
      limit?: number;
      include_inactive?: boolean;
    }
  ): Promise<IrrigationZoneListResponse> {
    const searchParams = new URLSearchParams();

    if (params?.skip !== undefined) {
      searchParams.append('skip', params.skip.toString());
    }
    if (params?.limit !== undefined) {
      searchParams.append('limit', params.limit.toString());
    }
    if (params?.include_inactive !== undefined) {
      searchParams.append('include_inactive', params.include_inactive.toString());
    }

    const queryString = searchParams.toString();
    const endpoint = `/irrigation-zones/farm/${farmId}${queryString ? `?${queryString}` : ''}`;

    return apiClient.get<IrrigationZoneListResponse>(endpoint);
  }

  /**
   * Get a specific irrigation zone by ID
   */
  async getIrrigationZone(zoneId: number): Promise<IrrigationZone> {
    return apiClient.get<IrrigationZone>(`/irrigation-zones/${zoneId}`);
  }

  /**
   * Get an irrigation zone with its associated sectors
   */
  async getIrrigationZoneWithSectors(
    zoneId: number,
    includeInactive?: boolean
  ): Promise<IrrigationZoneWithSectors> {
    const searchParams = new URLSearchParams();

    if (includeInactive !== undefined) {
      searchParams.append('include_inactive_memberships', includeInactive.toString());
    }

    const queryString = searchParams.toString();
    const endpoint = `/irrigation-zones/${zoneId}/with-sectors${queryString ? `?${queryString}` : ''}`;

    return apiClient.get<IrrigationZoneWithSectors>(endpoint);
  }

  /**
   * Create a new irrigation zone
   */
  async createIrrigationZone(data: IrrigationZoneCreate): Promise<IrrigationZone> {
    return apiClient.post<IrrigationZone>('/irrigation-zones', data);
  }

  /**
   * Update an existing irrigation zone
   */
  async updateIrrigationZone(
    zoneId: number,
    data: IrrigationZoneUpdate
  ): Promise<IrrigationZone> {
    return apiClient.put<IrrigationZone>(`/irrigation-zones/${zoneId}`, data);
  }

  /**
   * Delete an irrigation zone (soft-delete)
   */
  async deleteIrrigationZone(zoneId: number): Promise<void> {
    return apiClient.delete<void>(`/irrigation-zones/${zoneId}`);
  }

  /**
   * Add a sector to a zone
   */
  async addSectorToZone(
    zoneId: number,
    sectorId: number,
    leftAt?: string | null
  ): Promise<ZoneSectorMembership> {
    const data = {
      zone_id: zoneId,
      sector_id: sectorId,
      left_at: leftAt,
    };

    return apiClient.post<ZoneSectorMembership>(`/irrigation-zones/${zoneId}/sectors`, data);
  }

  /**
   * Remove a sector from a zone
   */
  async removeSectorFromZone(membershipId: number): Promise<void> {
    return apiClient.delete<void>(`/irrigation-zones/memberships/${membershipId}`);
  }

  /**
   * Get all sectors in a zone
   */
  async getZoneSectors(
    zoneId: number,
    params?: {
      skip?: number;
      limit?: number;
      include_inactive?: boolean;
    }
  ): Promise<ZoneSectorsListResponse> {
    const searchParams = new URLSearchParams();

    if (params?.skip !== undefined) {
      searchParams.append('skip', params.skip.toString());
    }
    if (params?.limit !== undefined) {
      searchParams.append('limit', params.limit.toString());
    }
    if (params?.include_inactive !== undefined) {
      searchParams.append('include_inactive', params.include_inactive.toString());
    }

    const queryString = searchParams.toString();
    const endpoint = `/irrigation-zones/${zoneId}/sectors${queryString ? `?${queryString}` : ''}`;

    return apiClient.get<ZoneSectorsListResponse>(endpoint);
  }

  /**
   * Get all zones that a sector belongs to
   */
  async getSectorZones(sectorId: number): Promise<ZoneSectorMembership[]> {
    return apiClient.get<ZoneSectorMembership[]>(`/irrigation-zones/sectors/${sectorId}/zones`);
  }

  /**
   * Bulk update sector zone assignments
   * Allows atomically updating multiple sector zone assignments in a single transaction
   */
  /**
   * Set which zones each sector belongs to. DECLARATIVE: the zones listed become the
   * sector's active memberships exactly, and anything omitted is left.
   *
   * Prefer `zone_ids` — a sector may belong to several zones. `zone_id` is the
   * original single-zone shape and is exactly `zone_ids: [N]` (or `[]` for null), so
   * it still behaves as a move; it is kept for callers that genuinely hold one zone.
   * Send one or the other, never both, and never neither — the backend rejects both
   * cases, the second because defaulting an absent field to "remove from every zone"
   * is how memberships got wiped silently.
   */
  async bulkUpdateSectorZoneAssignments(
    assignments: Array<
      | { sector_id: number; zone_ids: number[] }
      | { sector_id: number; zone_id: number | null }
    >
  ): Promise<ZoneSectorMembership[]> {
    return apiClient.post<ZoneSectorMembership[]>(
      '/irrigation-zones/bulk-update-zone-assignments',
      { assignments }
    );
  }

  /**
   * Get all zone sector memberships for a farm
   * Iterates through all zones and collects their sector memberships
   */
  async getZoneSectorMembershipsForFarm(farmId: string): Promise<ZoneSectorMembership[]> {
    const zones = await this.getIrrigationZones(farmId);
    const allMemberships: ZoneSectorMembership[] = [];
    
    for (const zone of zones.items) {
      try {
        const sectors = await this.getZoneSectors(zone.id);
        allMemberships.push(...sectors.items);
      } catch (error) {
        // Skip zones that don't exist or user doesn't have access to
        // This can happen if zones were deleted or there's a race condition
        console.warn(`Failed to fetch sectors for zone ${zone.id}:`, error);
      }
    }
    
    return allMemberships;
  }

  /**
   * Quick-irrigate (PR-UX2): preview which valves a zone would open if
   * irrigated. Read-only; doesn't change state. Use this before showing
   * the user a 'Will open: …' confirmation so the dispatch matches.
   */
  async getResolvedValves(zoneId: number): Promise<ZoneResolvedValves> {
    return apiClient.get<ZoneResolvedValves>(`/irrigation-zones/${zoneId}/resolved-valves`);
  }

  /**
   * Quick-irrigate (PR-UX2): create a transient one-time irrigation program
   * for a zone and start the run. Returns the new program/run IDs plus the
   * resolved valves so the caller can show what's being opened.
   */
  async quickIrrigate(
    zoneId: number,
    body: QuickIrrigateRequest,
  ): Promise<QuickIrrigateResponse> {
    return apiClient.post<QuickIrrigateResponse>(
      `/irrigation-zones/${zoneId}/quick-irrigate`,
      body,
    );
  }

  /**
   * The sector-backed half of quick-irrigate: the same preview and the same
   * dispatch, for an explicit list of sectors instead of a zone. Mirrors a
   * sector-backed program step, which is the other target a step has always
   * accepted.
   *
   * These live on /irrigation-sectors rather than /irrigation-zones — there is no
   * zone to key the path on — but they are the same feature, so they are kept
   * beside their zone twins rather than scattered into another client.
   *
   * POST for a read-only preview because the input is an ordered list, and the
   * widget sends the body it will dispatch with.
   */
  async getResolvedValvesForSectors(
    sectorIds: number[],
  ): Promise<ZoneResolvedValves> {
    return apiClient.post<ZoneResolvedValves>(
      '/irrigation-sectors/resolved-valves',
      { sector_ids: sectorIds },
    );
  }

  async quickIrrigateSectors(
    sectorIds: number[],
    body: QuickIrrigateRequest,
  ): Promise<QuickIrrigateResponse> {
    return apiClient.post<QuickIrrigateResponse>(
      '/irrigation-sectors/quick-irrigate',
      { ...body, sector_ids: sectorIds },
    );
  }
}

// --- Quick-irrigate (PR-UX2) types -----------------------------------------

export interface QuickIrrigateResolvedValve {
  dev_eui: string;
  /** NULL on an Opta, which addresses outputs by channel rather than by number.
   *  Typed as a required number, this made every Opta-backed zone fail response
   *  validation server-side with a 500 before any error reached the operator. */
  valve_number: number | null;
  sector_id: number;
  sector_name?: string | null;
  device_name?: string | null;
  /** ISO8601 timestamp of the device's most recent uplink; null if never seen.
   *  Used by the widget to flag stale (borderline-offline) devices. */
  device_last_seen?: string | null;
  /** Which family drives this valve — 'mqtt_opta' or 'lorawan'/null. */
  controller_transport?: string | null;
  /** Opta output binding; null on LoRaWAN hardware. */
  output_channel?: string | null;
}

export interface ZoneResolvedValves {
  /** NULL on a sector-backed preview — there is no zone. */
  zone_id?: number | null;
  zone_name?: string | null;
  /** Empty on a zone-backed preview. */
  sector_ids?: number[];
  /** Always populated: the zone's name, or the sectors', for messages. */
  target_name: string;
  resolved_valves: QuickIrrigateResolvedValve[];
}

export interface QuickIrrigateRequest {
  // Either duration_minutes OR (hours + minutes); backend resolves whichever.
  duration_minutes?: number;
  hours?: number;
  minutes?: number;
  notes?: string;
  // PR-UX4: when true, skip offline devices and run with only the available
  // valves; when false (default), 409 if any device is offline.
  confirm_offline_skip?: boolean;
  // Localized label for the transient program's name (e.g. "Quick irrigation" /
  // "Riego rápido"). The backend appends the zone name + farm-local timestamp.
  name_prefix?: string;
  /** Which irrigation head should run this. Omit to let the backend derive it from
   *  the programs that already water these sectors; it answers 409 rather than
   *  guessing when that is ambiguous or unknown, because starting valves with no
   *  head means irrigating dry. */
  pump_station_id?: number;
}

export interface QuickIrrigateResponse {
  program_id: number;
  run_id: number;
  /** NULL on a sector-backed run. */
  zone_id?: number | null;
  zone_name?: string | null;
  /** Empty on a zone-backed run. */
  sector_ids?: number[];
  /** Always populated — what to put in the "started" message without branching. */
  target_name: string;
  duration_minutes: number;
  resolved_valves: QuickIrrigateResolvedValve[];
  /** Which head was actually started, and by which path. */
  pump_station_id?: number | null;
  execution_strategy?: string | null;
}

/** One head the backend considers a plausible feed for a zone. */
export interface QuickIrrigateHeadCandidate {
  pump_station_id: number;
  pump_station_name: string;
  /** Short phrase naming the evidence, e.g. "program «Turno Norte» waters these
   *  sectors from it". Shown to the operator so a derived head explains itself. */
  reason: string;
}

/** Body of the 409 the backend returns when it will not guess the head. */
export interface QuickIrrigateHeadProblem {
  message: string;
  reason: 'pump_station_unresolved' | 'pump_station_ambiguous';
  zone_id?: number | null;
  sector_ids?: number[];
  candidates: QuickIrrigateHeadCandidate[];
}

// Create and export singleton instance
export const irrigationZonesApi = new IrrigationZonesApiService();

// Export class for testing purposes
export { IrrigationZonesApiService };

export default irrigationZonesApi;
